import React from "react";
import { Button } from "@mui/material";
import Section from "../common/Details";
import { JobsApi } from "../../../service";
import GenericModal from "../../Ui/Modal";
import ButtonComponent from "../../Ui/Button";
import { formatDate, humanizeEnum, formatTime, formatCurrency, resolveEstimatedRange, formatScheduledTime } from "../../utils/utils";
import useApiHandler from "../../hooks/ApiHandler";
import { useContext, useState, useEffect } from "react";
import { UserContext } from "../../context/UserContext";
import { headers } from "../../constant/Home/Dashboard";
import TableScreen from "../common/TableScreen/TableScreen";
import { searchbarVisibilty } from "../../constant/Home/Dashboard";
import { useCurrentSelectedData } from "../../context/CurrentSelectedDataContext";
import { useStatisticsData } from "../../context/StatisticsData";
import {
  generateAvailabeJob,
  generateConfirmedJobDetails,
  formatAddress,
} from "../../constant/Home/Jobs";


const AvailableJobs = () => {
  const { refreshStatistics } = useStatisticsData();
  const { userData } = useContext(UserContext);
  const [modalState, setModalState] = useState("");
  const [availableJobsSearch, setAvailableJobsSearch] = useState("");
  const { callApi, loading } = useApiHandler();
  const [jobs, setJobs] = React.useState([]);
  const { currentSelectedData, setCurrentSelectedData } =
    useCurrentSelectedData();
  const [currentPage, setCurrentPage] = useState(1);
  const [resultsPerPage, setResultsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const getAvailableJobs = async (opts = {}) => {
    const { silent } = opts;
    await callApi(() => JobsApi.getAvailableJobs(currentPage, resultsPerPage), {
      silent,
      onSuccess: (response) => {
        if (response.data) {
          if (!silent) console.log(response.data);
          setJobs(response.data?.results || response.data);
          setTotalCount(response.data?.count || response.data?.length || 0);
        }
      },
      onError: (error) => {
        console.log(error);
      },
    });
  };

  // Handle results per page change
  const handleResultsPerPageChange = (newResultsPerPage) => {
    setResultsPerPage(newResultsPerPage);
    setCurrentPage(1); // Reset to first page when changing results per page
  };

  useEffect(() => {
    getAvailableJobs();
  }, [currentPage, resultsPerPage]);

  // Refresh available jobs list during dashboard polling while this tab is mounted
  useEffect(() => {
    const onPoll = () => {
      getAvailableJobs({ silent: true }).catch(() => {});
    };
    window.addEventListener("dashboard:poll", onPoll);
    return () => window.removeEventListener("dashboard:poll", onPoll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, resultsPerPage]);


  const handleConfirmAndScheduleJob = async () => {
    if (!currentSelectedData?.id) {
      alert("No job selected.");
      return;
    }

    // Ensure a time has been selected from "Select a Time to Complete the Work"
    if (!currentSelectedData?.timeSelected) {
      alert("Please select at least one time to complete the work.");
      return;
    }

    const payload = {
      request: currentSelectedData.id,
      // Preserve the original job estimate range when present (e.g. "$150 - $250").
      // Some UI surfaces may show only the minimum, but the proposal summary should reflect the full range.
      amount: (() => {
        const raw =
          currentSelectedData?.priceEstimate ??
          currentSelectedData?.price_estimate ??
          currentSelectedData?.estimatedRange ??
          currentSelectedData?.estimated_range ??
          null;

        if (typeof raw === "string" && /[-–]/.test(raw)) return raw.trim();

        const resolved = resolveEstimatedRange(currentSelectedData);
        if (typeof resolved === "string" && /[-–]/.test(resolved)) return resolved;

        return raw ?? resolved;
      })(),
      time_frame: currentSelectedData.timeSelected,
      message: generateConfirmationMessage({
        vendorName:
          userData?.company_name || userData?.companyName || userData?.fullName || userData?.full_name || userData?.name || "",
        jobType: humanizeEnum(currentSelectedData.subIssueType),
        scheduledTime:
          currentSelectedData?.timeSelected || "the scheduled time we agreed",
      }),
    };

    await callApi(() => JobsApi.confirmAndScheduleJob(payload), {
      onSuccess: (response) => {
        console.log("Job confirmed and scheduled successfully:", response);
        // Remove the confirmed job from the available jobs list
        setJobs((prevJobs) =>
          prevJobs.filter((job) => job.id !== currentSelectedData.id)
        );
        setModalState(MODAL_STATES.JOB_CONFIRMED);
        refreshStatistics();
        // Notify other parts of the app (e.g., Dashboard) to refresh messages immediately
        window.dispatchEvent(new Event("messages:refresh"));
      },
      onError: (error) => {
        console.error("Error confirming job:", error);
        alert("Failed to confirm and schedule job. Please try again.");
      },
    });
  };

  const MODAL_STATES = {
    JOB_DETAILS: "jobDetails",
    JOB_CONFIRMED: "jobConfirmed",
  };

  // When the confirmation modal opens, fetch full request details to enrich address
  useEffect(() => {
    const shouldFetch =
      modalState === MODAL_STATES.JOB_CONFIRMED && currentSelectedData?.id;
    if (!shouldFetch) return;

    (async () => {
      await callApi(() => JobsApi.getMaintenanceRequest(currentSelectedData.id), {
        onSuccess: (resp) => {
          const full = resp?.data || resp;
          if (!full) return;
          // Merge fetched payload into currentSelectedData so modal shows full address
          setCurrentSelectedData({
            ...currentSelectedData,
            ...full,
            // Preserve the selected time used for display
            timeSelected: currentSelectedData?.timeSelected,
          });
        },
        onError: () => {
          // No-op; keep existing snapshot if fetch fails
        },
      });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalState, currentSelectedData?.id]);
  const footer = {
    [MODAL_STATES.JOB_DETAILS]: (
      <div className="flex w-full justify-between items-center">
        <ButtonComponent onClick={() => setModalState("")}>
          Back
        </ButtonComponent>

        <div className="flex flex-col items-end">
          <ButtonComponent
            onClick={handleConfirmAndScheduleJob}
            bgColor="#3CAB5C"
            color="#FFFFFF"
            disabled={loading || !currentSelectedData?.timeSelected}
          >
            {loading
              ? "Processing..."
              : !currentSelectedData?.timeSelected
              ? "Please schedule a time"
              : "Confirm & Schedule Job"}
          </ButtonComponent>
        </div>
      </div>
    ),
    [MODAL_STATES.JOB_CONFIRMED]: (
      <div className="flex w-full justify-between">
        <div>{/* intentional placeholder div */}</div>
        <ButtonComponent
          bgColor="#3CAB5C"
          color="#FFFFFF"
          onClick={() => setModalState("")}
        >
          Exit
        </ButtonComponent>
      </div>
    ),

    // Remove denial reason footer since it's now inside the form
  };

  const content = {
    [MODAL_STATES.JOB_DETAILS]: (
      <Section sections={generateAvailabeJob(currentSelectedData)} />
    ),
    [MODAL_STATES.JOB_CONFIRMED]: (
      <Section sections={generateConfirmedJobDetails(currentSelectedData)} />
    ),
  };

  const handleClose = () => {
    setModalState("");

    if (modalState === MODAL_STATES.JOB_CONFIRMED) {
      getAvailableJobs();
    }
  };

  const title = {
    [MODAL_STATES.JOB_DETAILS]: "Job Details",
    [MODAL_STATES.JOB_CONFIRMED]: "Summary of Job  Details",
  };

  const maybeHumanizeDisplay = (val) => {
    if (!val || typeof val !== "string") return val;
    const trimmed = val.trim();
    if (!trimmed) return trimmed;
    // Only humanize when the backend "display" value still looks like an enum.
    const looksLikeEnum = trimmed.includes("_") || trimmed === trimmed.toUpperCase();
    return looksLikeEnum ? humanizeEnum(trimmed) : trimmed;
  };

  const renderAvailabeJobsCell = (row, header, colIdx, options = {}) => {
    console.log(row, "row");
    const { title, setModalState } = options;
    switch (header) {
      case "DATE ISSUED":
        return formatDate(row?.createdAt);
      case "TIME ISSUED": {
        const source =
          row?.createdAt || row?.created_at || row?.timeIssued || row?.availableFrom;
        return formatTime(source);
      }
      case "JOB CATEGORY": {
        // Show high-level issue type (e.g., Roofing, Plumbing)
        const issue = humanizeEnum(row?.issueType || row?.issue_type);
        return issue || "N/A";
      }
      case "JOB TYPE": {
        // Show readable sub-issue type, prefer server-provided display label
        const rawSubType =
          row?.subIssueTypeDisplay ||
          row?.sub_issue_type_display ||
          row?.subIssueType ||
          row?.sub_issue_type;

        const subType = maybeHumanizeDisplay(rawSubType);
        return subType || "N/A";
      }
      case "LOCATION": {
        // Prefer a structured address object and format it nicely
        const addrObj =
          row?.propertyAddress ||
          row?.property_address ||
          row?.jobSnapshot?.propertyAddress ||
          row?.jobSnapshot?.property_address ||
          row?.requestSnapshot?.propertyAddress ||
          row?.requestSnapshot?.property_address ||
          null;

        if (addrObj && typeof addrObj === "object") {
          const formatted = formatAddress(addrObj);
          return formatted || "N/A";
        }

        // Fallbacks: sometimes APIs may send a pre-formatted string
        const flatVal =
          (typeof row?.propertyAddress === "string" && row?.propertyAddress) ||
          (typeof row?.jobSnapshot?.propertyAddress === "string" &&
            row?.jobSnapshot?.propertyAddress) ||
          (typeof row?.requestSnapshot?.propertyAddress === "string" &&
            row?.requestSnapshot?.propertyAddress) ||
          row?.location ||
          row?.propertyAddress?.streetAddress ||
          "N/A";
        return flatVal;
      }
      case "JOB TITLE": {
        // Legacy: some views might still use JOB TITLE; mirror JOB TYPE behavior
        const rawTitle =
          row?.subIssueTypeDisplay ||
          row?.sub_issue_type_display ||
          row?.subIssueType ||
          row?.sub_issue_type ||
          row?.issueType ||
          row?.issue_type;

        const title = maybeHumanizeDisplay(rawTitle);
        return title || "N/A";
      }
      // return row?.issueType + row?.subIssueType;
      case "JOB DESCRIPTION":
        return (
          row?.description ||
          "No description provided"
        );
      case "ORIGINAL ESTIMATE RANGE": {
        const value = resolveEstimatedRange(row);
        return value || "No estimated range provided";
      }

      case "VIEW DETAILS TO ACCEPT":
        return (
          <Button
            variant="outlined"
            size="small"
            style={{
              borderRadius: 8,
              textTransform: "none",
              border: "1px solid #ECECEC",
              color: "#242424",
            }}
            onClick={() => {
              setCurrentSelectedData(row);
              setModalState("jobDetails");
            }}
          >
            View
          </Button>
        );

      case "VIEW":
        return (
          <Button
            variant="outlined"
            size="small"
            style={{
              borderRadius: 8,
              textTransform: "none",
              border: "1px solid #ECECEC",
              color: "#242424",
            }}
          >
            View
          </Button>
        );

      default:
        return row[header.toLowerCase()] || "N/A";
    }
  };

  const filteredRows = React.useMemo(() => {
    if (!availableJobsSearch) return jobs;
    const searchLower = availableJobsSearch.toLowerCase();

    return jobs.filter((row) => {
      return (
        row?.id?.toLowerCase().includes(searchLower) ||
        row?.propertyAddress?.toLowerCase().includes(searchLower) ||
        row?.jobTitle?.toLowerCase().includes(searchLower)
      );
    });
  }, [availableJobsSearch, jobs]);

  return (
    <>
      <TableScreen
        title="Available Jobs"
        // description="View all tenant repair requests that require attention"
        placeholder="Search by job number, property address, job title"
        headers={headers.availableJobs}
        rows={filteredRows}
        renderCell={(row, header, colIdx) =>
          renderAvailabeJobsCell(row, header, colIdx, {
            title: "Available Jobs",
            setModalState: () => setModalState(MODAL_STATES.JOB_DETAILS),
          })
        }
        onSearch={setAvailableJobsSearch}
        search={searchbarVisibilty[userData.accountType]?.availableJobs}
        // Pagination props
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        resultsPerPage={resultsPerPage}
        onResultsPerPageChange={handleResultsPerPageChange}
        totalCount={totalCount}
        useExternalPagination={true}
      />
      <GenericModal
        logo={modalState === MODAL_STATES.JOB_CONFIRMED}
        logoDetail={
          modalState === MODAL_STATES.JOB_CONFIRMED
            ? "You're Booked! Time to Get to Work."
            : null
        }
        open={Boolean(modalState)}
        onClose={handleClose}
        title={title[modalState]}
        children={content[modalState]}
        footer={footer[modalState] || null}
        maxWidth="953px"
      />
    </>
  );
};

const generateConfirmationMessage = ({ vendorName, jobType, scheduledTime }) => {
  const safeVendor = vendorName?.trim();
  const safeJob = jobType?.trim() || "maintenance";
  const formattedSchedule = formatScheduledTime(scheduledTime) || "the scheduled time we agreed";
  const greeting = safeVendor ? `Hello this is ${safeVendor}, We're` : "We're";

  return `${greeting} looking forward to completing your ${safeJob} job on ${formattedSchedule}. You can contact us through this message thread at any time!`;
};

export default AvailableJobs;
